import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { IAppointment } from '../../../../core/models/appointment.model';
import { EAppointmentStatus } from '../../../../core/enums/appointment-status.enum';
import { ICoachAppointmentNote } from '../../../../core/models/appointment-note.model';
import { EActorType } from '@gray/domain/models/coach/coach-task.model';
import { CoachAppointmentNoteService } from '../../../../core/services/coach-appointment-note.service';
import { CoachCalendarService } from '../../../../core/services/coach-calendar.service';

const PAGE_SIZE = 10;
const MAX_LENGTH = 4000;
/** How many past meetings the form offers to write against. */
const PICKABLE_MEETINGS = 3;

/**
 * Notes written against a meeting: pick which meeting, write, and read the history back with the
 * meeting each note belongs to. The student can read these, which is what separates them from the
 * coach's private notes in the other panel.
 */
@Component({
  selector: 'coach-appointment-notes-panel',
  templateUrl: './appointment-notes-panel.component.html',
  styleUrl: './appointment-notes-panel.component.scss',
})
export class AppointmentNotesPanelComponent implements OnChanges, OnDestroy {
  @Input() studentId!: number;

  /** Past meetings, newest first; only these may carry a new note. */
  meetings: IAppointment[] = [];
  selectedMeetingId?: number;

  notes: ICoachAppointmentNote[] = [];
  page = 1;
  lastPage = 1;

  maxLength = MAX_LENGTH;

  fg: FormGroup = this.formBuilder.group({
    content: [null, [Validators.required, Validators.maxLength(MAX_LENGTH)]],
  });

  editingNoteId?: number;
  editControl = new FormControl<string>('', [Validators.required, Validators.maxLength(MAX_LENGTH)]);

  isLoading = false;
  meetingsIsLoading = false;
  isSaving = false;
  isSubmitted = false;
  editIsSaving = false;

  actorTypes = EActorType;

  destroyed$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private noteService: CoachAppointmentNoteService,
    private calendarService: CoachCalendarService,
    private toastrService: ToastrService,
    private translate: TranslateService,
  ) {
  }

  ngOnChanges(): void {
    if (this.studentId) {
      this.page = 1;
      this.notes = [];
      this.getMeetings();
      this.getNotes();
    }
  }

  hasMore(): boolean {
    return this.page < this.lastPage;
  }

  remainingLength(): number {
    return MAX_LENGTH - (this.fg.value.content?.length ?? 0);
  }

  /**
   * A note belongs to a meeting that has happened, so the picker only offers past ones — the most
   * recent first, which is also the one selected.
   */
  getMeetings() {
    this.meetingsIsLoading = true;
    this.calendarService.getAppointments()
      .pipe(takeUntil(this.destroyed$), finalize(() => this.meetingsIsLoading = false))
      .subscribe(r => {
        const now = Date.now();
        this.meetings = ((r.data ?? []) as IAppointment[])
          .filter(appointment => appointment.studentUserId === this.studentId)
          .filter(appointment => appointment.status !== EAppointmentStatus.Cancelled)
          .filter(appointment => new Date(appointment.startsAt).getTime() <= now)
          .sort((first, second) => new Date(second.startsAt).getTime() - new Date(first.startsAt).getTime())
          .slice(0, PICKABLE_MEETINGS);

        this.selectedMeetingId = this.meetings[0]?.id;
      });
  }

  getNotes(append = false) {
    this.isLoading = true;
    this.noteService.getStudentNotes(this.studentId, `?page=${this.page}&size=${PAGE_SIZE}`)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const result = (r.data?.result ?? []) as ICoachAppointmentNote[];
        this.notes = append ? [...this.notes, ...result] : result;
        this.lastPage = r.data?.meta?.lastPage ?? 1;
      });
  }

  loadMore() {
    this.page += 1;
    this.getNotes(true);
  }

  selectMeeting(meeting: IAppointment) {
    this.selectedMeetingId = meeting.id;
  }

  onSubmit() {
    this.isSubmitted = true;
    if (this.fg.invalid || !this.selectedMeetingId) {
      return;
    }

    this.isSaving = true;
    this.noteService.createNote(this.selectedMeetingId, this.fg.value.content)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isSaving = false))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('students.meeting_notes.appointment.saved'));
        this.isSubmitted = false;
        this.fg.reset();
        this.page = 1;
        this.getNotes();
      });
  }

  startEdit(note: ICoachAppointmentNote) {
    this.editingNoteId = note.id;
    this.editControl.setValue(note.content);
  }

  cancelEdit() {
    this.editingNoteId = undefined;
    this.editControl.reset();
  }

  saveEdit(note: ICoachAppointmentNote) {
    if (this.editControl.invalid) {
      return;
    }

    this.editIsSaving = true;
    this.noteService.updateNote(note.id, this.editControl.value)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.editIsSaving = false))
      .subscribe(() => {
        note.content = this.editControl.value;
        this.toastrService.success(this.translate.instant('students.meeting_notes.appointment.updated'));
        this.cancelEdit();
      });
  }

  async confirmDelete(note: ICoachAppointmentNote) {
    const Swal = (await import('sweetalert2')).default;

    const result = await Swal.fire({
      text: this.translate.instant('students.meeting_notes.appointment.delete_confirm'),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('buttons.yes'),
      cancelButtonText: this.translate.instant('buttons.cancel_l'),
      heightAuto: false,
    });

    if (result.isConfirmed) {
      this.deleteNote(note);
    }
  }

  deleteNote(note: ICoachAppointmentNote) {
    this.noteService.deleteNote(note.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('students.meeting_notes.appointment.deleted'));
        this.notes = this.notes.filter(current => current.id !== note.id);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
